import useShopSocket from '../hooks/useShopSocket';
import {setStaffDuties} from '../store/authSlice';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, Button, Dropdown, ProgressBar, Modal, Form, Alert } from 'react-bootstrap';
import { setMpinSet, logout } from '../store/authSlice';
import { fetchShopStart, fetchShopSuccess, fetchShopFailure, setShopSettings, clearShop } from '../store/shopSlice';
import { clearAuthState } from '../utils/tokenStorage';
import api from '../services/api';
import { getShopSettings } from '../services/settings';
import { updateShopOpenStatus } from '../services/shop';
import useShopTheme from '../hooks/useShopTheme';
import BrandLogo from './brand/BrandLogo';
import { resolveAssetUrl } from '../utils/media';
import { getPublicDesign } from '../utils/publicPageDesign';
import { getStatusReasons, OTHER_STATUS_REASON, resolveStatusReason } from '../constants/shopStatusReasons';
import './ShopHeaders.css';
import DashboardNavLinks from './DashboardNavLinks';
import {staffHeaderNavigation} from '../utils/dashboardNavigation.mjs';
import Spinner from './brand/BrandSpinner';

const DEFAULT_WARNING_DAYS = Number.isFinite(Number(import.meta.env.VITE_SHOP_CERTIFICATE_WARNING_DAYS)) ? Number(import.meta.env.VITE_SHOP_CERTIFICATE_WARNING_DAYS) : 5;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const DEFAULT_LANDING_PALETTE = {
  primary: '#02142C',
  accent: '#FF7300',
  onPrimary: '#FFFFFF',
  background: '#FFFFFF',
  text: '#02142C',
  fontSecondary: '#FFFFFF'
};

const SHOP_HEADER_ROLES = ['SHOP_OWNER', 'SHOP_STAFF', 'SHOP_OPERATOR', 'CHEF', 'DELIVERY', 'BEARER', 'HOTEL_USER'];

const TopNav = ({ brandLogoUrl, brandLogoBackground, brandLogoTransparent = false, brandLogoWidth = 40, brandLogoHeight = 40, landingPalette = DEFAULT_LANDING_PALETTE }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { navLinks } = useSelector((state) => state.ui);
  const { user } = useSelector((state) => state.auth);
  const currentUserId = useRef(user?.id);
  currentUserId.current = user?.id;
  const shopProfile = useSelector((state) => state.shop?.profile);
  const shopSettings = useSelector((state) => state.shop?.settings);
  const shopStatus = useSelector((state) => state.shop?.status || 'idle');
  const { palette, isActive: isShopThemeActive } = useShopTheme();
  const [showNotifications, setShowNotifications] = useState(false);

  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  const [statusOtherReason, setStatusOtherReason] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const navToggleRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(()=>window.matchMedia('(max-width: 991.98px)').matches);
  useEffect(()=>{setNavOpen(false);},[location.pathname,location.search,user?.id]);
  useEffect(()=>{
    const media=window.matchMedia('(max-width: 991.98px)');
    const changed=()=>{setIsMobileView(media.matches);setNavOpen(false);};
    media.addEventListener('change',changed);
    return()=>media.removeEventListener('change',changed);
  },[]);
  useEffect(()=>{
    if(!navOpen || !isMobileView) return undefined;
    const close=event=>{if(event.key==='Escape'){setNavOpen(false);navToggleRef.current?.focus();}};
    const previous=document.body.style.overflow;
    document.body.style.overflow='hidden';
    document.addEventListener('keydown',close);
    return()=>{document.body.style.overflow=previous;document.removeEventListener('keydown',close);};
  },[navOpen,isMobileView]);
  const [mpinVerifiedFor,setMpinVerifiedFor]=useState(null);
  const [mpinModalOpen, setMpinModalOpen] = useState(false);
  const [mpinPromptMode, setMpinPromptMode] = useState('manual');
  const [mpinForm, setMpinForm] = useState({ currentMpin: '', mpin: '', confirm: '' });
  const [mpinStatus, setMpinStatus] = useState('idle');
  const [mpinMessage, setMpinMessage] = useState('');
  const [subscriptionRenewalAlert, setSubscriptionRenewalAlert] = useState(null);
  const isStaffUser = user?.role === 'SHOP_OPERATOR';
  useShopSocket(isStaffUser?user?.shopId:null,{'staff:access':()=>window.dispatchEvent(new Event('staff-access:refresh'))});
  const [staffAccount, setStaffAccount] = useState(null);
  const [staffProfileOpen, setStaffProfileOpen] = useState(false);
  useEffect(() => {
    setStaffAccount(null); setStaffProfileOpen(false); setShowNotifications(false);
    if (!isStaffUser) return undefined;
    let active = true;
    let loadingAccess=false;
    let refreshAgain=false;
    const load = () => {
      if(document.visibilityState==='hidden')return;
      if(loadingAccess){refreshAgain=true;return;}
      loadingAccess=true;
      // Duty loading must not depend on activity or notification queries.
      const access=api.get('/staff/access').then(({data})=>{if(active && data.userId===user.id && Array.isArray(data.assigned))dispatch(setStaffDuties({userId:user.id,duties:data.assigned}));});
      const profile=api.get('/staff/me').then(({data})=>{if(active)setStaffAccount({...data,userId:user.id});});
      Promise.allSettled([access,profile]).finally(()=>{loadingAccess=false;if(active&&refreshAgain){refreshAgain=false;load();}});
    };
    load(); const timer = setInterval(load, 30000);
    window.addEventListener('focus',load);
    window.addEventListener('staff-access:refresh',load);
    document.addEventListener('visibilitychange',load);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus',load); window.removeEventListener('staff-access:refresh',load); document.removeEventListener('visibilitychange',load); };
  }, [isStaffUser, user?.id, dispatch]);
  const ownStaffAccount = staffAccount?.userId === user?.id ? staffAccount : null;
  const headerNavLinks=useMemo(()=>isStaffUser?staffHeaderNavigation(navLinks,user?.staffModules||ownStaffAccount?.user?.staffModules||[]):navLinks,[isStaffUser,navLinks,ownStaffAccount,user?.staffModules]);
  const accountPhoto = resolveAssetUrl(isStaffUser ? ownStaffAccount?.user?.profilePhotoUrl || user?.profilePhotoUrl : user?.profilePhotoUrl);

  const handleLogout = () => {
    clearAuthState();
    dispatch(logout());
    navigate('/', { replace: true });
  };

  const handleToggleShopStatus = async () => {
    if (!isShopUser || !shopProfile) {
      return;
    }
    const nextStatus = !shopIsOpen;
    const statusRemark = resolveStatusReason(statusReason, statusOtherReason);
    if (statusRemark.length < 3) {
      setStatusError('Select a reason or enter a valid other reason.');
      return;
    }
    try {
      setStatusUpdating(true);
      setStatusError('');
      if (shopProfile.statusAutomation?.enabled) {
        const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
        await api.put(`/shops/me/status-calendar/${today}`, { isOpen: nextStatus, remark: statusRemark.trim() });
      } else {
        await updateShopOpenStatus({ isOpen: nextStatus, reason: statusRemark.trim() });
      }
      const { data } = await api.get('/shops/me', { params: { _cb: Date.now() } });
      dispatch(fetchShopSuccess(data));
      setStatusModalOpen(false);
      setStatusReason('');
      setStatusOtherReason('');
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to update shop status.';
      setStatusError(message);
    } finally {
      setStatusUpdating(false);
    }
  };

  const openMpinModal = (mode = 'manual') => {
    setMpinPromptMode(mode);
    setMpinForm({ currentMpin: '', mpin: '', confirm: '' });
    setMpinStatus('idle');
    setMpinMessage('');
    setMpinModalOpen(true);
  };

  const closeMpinModal = () => {
    setMpinModalOpen(false);
    setMpinPromptMode('manual');
  };

  const handleMpinChange = (event) => {
    const { name, value } = event.target;
    setMpinForm((prev) => ({ ...prev, [name]: value.replace(/[^0-9]/g, '').slice(0, 6) }));
  };

  const handleMpinSubmit = async (event) => {
    event.preventDefault();
    if (user?.mpinSet && mpinForm.currentMpin.length !== 6) {
      setMpinStatus('failed');
      setMpinMessage('Enter your current 6-digit MPIN.');
      return;
    }
    if (mpinForm.mpin.length !== 6 || mpinForm.confirm.length !== 6) {
      setMpinStatus('failed');
      setMpinMessage('MPIN must be exactly 6 digits.');
      return;
    }
    if (mpinForm.mpin !== mpinForm.confirm) {
      setMpinStatus('failed');
      setMpinMessage('MPIN and confirm MPIN must match.');
      return;
    }

    setMpinStatus('loading');
    setMpinMessage('');
    try {
      const savingUserId=user.id;
      const {data:saved}=await api.post('/auth/mpin', {
        mpin: mpinForm.mpin,
        currentMpin: user?.mpinSet ? mpinForm.currentMpin : undefined
      });
      if(saved?.mpinSet!==true)throw new Error('The server did not confirm MPIN setup. Please try again.');
      const {data:verified}=await api.get('/auth/security',{headers:{'Cache-Control':'no-cache'}});
      if(verified?.mpinSet!==true)throw new Error('MPIN setup could not be verified. Please try again.');
      if(currentUserId.current!==savingUserId)return;
      dispatch(setMpinSet(true));setMpinVerifiedFor(savingUserId);
      setMpinStatus('success');
      setMpinMessage('MPIN saved successfully.');
      setMpinForm({ currentMpin: '', mpin: '', confirm: '' });
      window.setTimeout(() => closeMpinModal(), 700);
    } catch (error) {
      setMpinStatus('failed');
      setMpinMessage(error?.response?.data?.message || error.message || 'Failed to save MPIN.');
    }
  };

  const displayName = useMemo(() => {
    if (!user) return '';
    if (user.name) return user.name;
    const composed = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (composed) return composed;
    if (user.username) return user.username;
    if (user.email) return user.email;
    return 'User';
  }, [user]);

  const initials = useMemo(() => {
    if (!displayName) return 'U';
    const parts = displayName.trim().split(/\s+/).slice(0, 2);
    const letters = parts.map((part) => part[0]?.toUpperCase()).filter(Boolean);
    if (letters.length === 0 && user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return letters.join('') || 'U';
  }, [displayName, user]);

  const isAdmin = user?.role === 'ADMIN';
  const isShopUser = SHOP_HEADER_ROLES.includes(user?.role);
  useEffect(() => {
    if (user?.role !== 'SHOP_OWNER') {
      setSubscriptionRenewalAlert(null);
      return;
    }
    let active = true;
    const loadRenewalAlert = () => api.get('/billing/overview')
      .then(({ data }) => { if (active) setSubscriptionRenewalAlert(data.renewalAlert || null); })
      .catch(() => { if (active) setSubscriptionRenewalAlert(null); });
    loadRenewalAlert();
    const timer = window.setInterval(loadRenewalAlert, 5 * 60 * 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, [user?.role]);
  const brandLogoSrc = brandLogoUrl || null;
  const rawShopLogo = typeof shopProfile?.logoUrl === 'string'
    ? shopProfile.logoUrl
    : typeof shopProfile?.logo === 'string'
      ? shopProfile.logo
      : '';
  const shopLogoSrc = isShopUser ? resolveAssetUrl(rawShopLogo) : '';
  const hasShopLogo = Boolean(shopLogoSrc);
  const shopHeaderDesign = getPublicDesign(shopProfile?.publicPageDesign);
  const shopBrandNameImageSrc = isShopUser ? resolveAssetUrl(shopProfile?.brandNameImageUrl) : '';

  const shopName = useMemo(() => {
    if (shopProfile?.name) return shopProfile.name;
    if (shopProfile?.shopName) return shopProfile.shopName;
    if (shopProfile?.businessName) return shopProfile.businessName;
    return 'SmartMenu';
  }, [shopProfile]);
  const shopIsOpen = shopProfile?.openStatus?.isOpen !== false;
  const StatusSwitch = ({ className = '', showError = false }) => {
    if (!isShopUser || user?.role === 'SHOP_OPERATOR') {
      return null;
    }
    return (
      <div className={`d-flex flex-column ${className}`.trim()}>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={shopIsOpen}
            className={`shop-status-button ${shopIsOpen ? 'is-open' : 'is-closed'}`}
            onClick={() => {
              const reasons = getStatusReasons(!shopIsOpen);
              setStatusReason(reasons[0]);
              setStatusOtherReason('');
              setStatusError('');
              setStatusModalOpen(true);
            }}
            disabled={statusUpdating}
          >
            <span className="shop-status-dot" aria-hidden="true" />
            {statusUpdating ? 'Updating...' : shopIsOpen ? 'Open' : 'Close'}
          </button>
          {statusUpdating && <Spinner animation="border" size="sm" />}
        </div>
        {showError && statusError && (
          <div className="text-danger small mt-1" style={{ maxWidth: 220 }}>
            {statusError}
          </div>
        )}
      </div>
    );
  };

  const canUseMpin = Boolean(user) && user.role !== 'ADMIN';

  const needProfile = isShopUser && !shopProfile && shopStatus === 'idle';
  const needSettings = isShopUser && !isStaffUser && !shopSettings;

  useEffect(() => {
    if (!isShopUser || (!needProfile && !needSettings)) {
      return;
    }

    const loadingUserId = user?.id;
    const loadShopData = async () => {
      if (needProfile) {
        dispatch(fetchShopStart());
        try {
          const { data } = await api.get('/shops/me', {
            headers: { 'Cache-Control': 'no-cache' },
            params: { _cb: Date.now() }
          });
          if (currentUserId.current === loadingUserId) dispatch(fetchShopSuccess(data));
        } catch (error) {
          const message = error?.response?.data?.message || 'Failed to load shop profile';
          if (currentUserId.current === loadingUserId) dispatch(fetchShopFailure(message));
        }
      }

      if (needSettings) {
        try {
          const settings = await getShopSettings();
          if (currentUserId.current === loadingUserId) dispatch(setShopSettings(settings || null));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to load shop settings', error);
        }
      }
    };

    loadShopData();
  }, [dispatch, isShopUser, needProfile, needSettings, user?.id]);

  useEffect(()=>{
    if(!canUseMpin||!user?.id)return;
    let active=true;const id=user.id;setMpinVerifiedFor(null);
    api.get('/auth/security',{headers:{'Cache-Control':'no-cache'}}).then(({data})=>{if(active&&currentUserId.current===id&&typeof data?.mpinSet==='boolean'){dispatch(setMpinSet(data.mpinSet));setMpinVerifiedFor(id);}}).catch(()=>{/* Avoid setup prompts when credential status is unavailable. */});
    return()=>{active=false;};
  },[canUseMpin,user?.id,dispatch]);
  useEffect(() => {
    if (!canUseMpin || mpinVerifiedFor!==user?.id || user?.mpinSet || mpinModalOpen) {
      return;
    }
    const promptKey = `smartmenu:mpin-prompt:${user?.id || user?._id || user?.email || 'user'}`;
    if (window.sessionStorage.getItem(promptKey) === 'shown') {
      return;
    }
    window.sessionStorage.setItem(promptKey, 'shown');
    openMpinModal('first-login');
  }, [canUseMpin, user, mpinModalOpen,mpinVerifiedFor]);
  const warningDays = useMemo(() => {
    const rawFromSettings =
      (shopSettings && (shopSettings.certificateExpiryWarningDays ?? shopSettings.certificateWarningDays ?? shopSettings.expiryWarningDays)) ??
      (shopSettings?.team?.certificateExpiryWarningDays ?? shopSettings?.team?.expiryWarningDays);
    const rawFromProfile =
      (shopProfile?.settings && (shopProfile.settings.certificateExpiryWarningDays ?? shopProfile.settings.certificateWarningDays)) ??
      shopProfile?.certificateExpiryWarningDays;
    const raw = rawFromSettings ?? rawFromProfile;
    const value = Number(raw);
    if (Number.isFinite(value) && value >= 0) {
      return value;
    }
    return DEFAULT_WARNING_DAYS;
  }, [shopSettings, shopProfile]);

  const dateFormatter = useMemo(() => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }), []);

  const parseDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date;
  };

  const startOfToday = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const allNotifications = useMemo(() => {
    if (isStaffUser) return (ownStaffAccount?.notifications || []).map(row => ({ key: 'notification:'+row._id, title: row.title, message: row.body, date: new Date(row.createdAt), actionPath:row.type==='DELIVERY_ASSIGNED'?'/dashboard/delivery':'/dashboard/workspace' }));
    if (!shopProfile) return [];
    const entries = [];
    if (subscriptionRenewalAlert) {
      entries.push({
        key: 'subscription-renewal',
        title: 'Package renewal',
        message: subscriptionRenewalAlert.message,
        date: parseDate(subscriptionRenewalAlert.expiryDate) || startOfToday,
        type: 'subscription-renewal',
        status: subscriptionRenewalAlert.daysRemaining <= 0 ? 'expired' : 'expiring',
        actionPath: '/dashboard/settings#plan-billing',
        actionLabel: 'Renew package'
      });
    }
    const thresholdDate = new Date(startOfToday);
    thresholdDate.setDate(thresholdDate.getDate() + warningDays);

    const certificates = Array.isArray(shopProfile.certificates) ? shopProfile.certificates : [];
    certificates.forEach((certificate, index) => {
      const validTo =
        certificate?.validTo ||
        certificate?.expiresOn ||
        certificate?.expiryDate ||
        certificate?.validTill;
      const parsed = parseDate(validTo);
      if (!parsed) {
        return;
      }
      let status = null;
      if (parsed < startOfToday) {
        status = 'expired';
      } else if (parsed <= thresholdDate) {
        status = 'expiring';
      } else {
        return;
      }
      const formattedDate = dateFormatter.format(parsed);
      let message = '';
      if (status === 'expired') {
        message = `${certificate?.name || 'Certificate'} expired on ${formattedDate}.`;
      } else {
        const remainingDays = Math.max(0, Math.ceil((parsed.getTime() - startOfToday.getTime()) / MS_PER_DAY));
        if (remainingDays === 0) {
          message = `${certificate?.name || 'Certificate'} expires today (${formattedDate}).`;
        } else {
          message = `${certificate?.name || 'Certificate'} expires on ${formattedDate} (in ${remainingDays} day${remainingDays === 1 ? '' : 's'}).`;
        }
      }
      entries.push({
        key: `certificate-${certificate?._id || index}`,
        title: certificate?.name || 'Certificate',
        message,
        date: parsed,
        actionPath:'/dashboard/profile',
        type: 'certificate',
        status
      });
    });

    const teamMembers = Array.isArray(shopProfile.team) ? shopProfile.team : [];
    teamMembers.forEach((member, index) => {
      const certificate = member?.healthCertificate || {};
      const validTo =
        certificate?.validTo ||
        member?.certificateValidTo ||
        certificate?.expiresOn ||
        certificate?.expiryDate;
      const parsed = parseDate(validTo);
      if (!parsed) {
        return;
      }
      let status = null;
      if (parsed < startOfToday) {
        status = 'expired';
      } else if (parsed <= thresholdDate) {
        status = 'expiring';
      } else {
        return;
      }
      const memberName = member?.name || `Team member ${index + 1}`;
      const formattedDate = dateFormatter.format(parsed);
      let message = '';
      if (status === 'expired') {
        message = `${memberName}'s health certificate expired on ${formattedDate}.`;
      } else {
        const remainingDays = Math.max(0, Math.ceil((parsed.getTime() - startOfToday.getTime()) / MS_PER_DAY));
        if (remainingDays === 0) {
          message = `${memberName}'s health certificate expires today (${formattedDate}).`;
        } else {
          message = `${memberName}'s health certificate expires on ${formattedDate} (in ${remainingDays} day${remainingDays === 1 ? '' : 's'}).`;
        }
      }
      entries.push({
        key: `team-certificate-${member?._id || index}`,
        title: `${memberName} - Health Certificate`,
        message,
        date: parsed,
        actionPath:'/dashboard/staff',
        type: 'team-certificate',
        status
      });
    });

    return entries.sort((a, b) => {
      if (a.status !== b.status) {
        return a.status === 'expired' ? -1 : 1;
      }
      return a.date - b.date;
    });
  }, [isStaffUser, ownStaffAccount, shopProfile, startOfToday, warningDays, dateFormatter, subscriptionRenewalAlert]);

  const [dismissedState,setDismissedState]=useState({userId:null,keys:[]});
  const [notificationBusy,setNotificationBusy]=useState(false);
  const [notificationError,setNotificationError]=useState('');
  useEffect(()=>{let active=true;setNotificationError('');if(!user?.id)return undefined;api.get('/auth/notifications/dismissed').then(({data})=>{if(active)setDismissedState({userId:user.id,keys:data.keys || []});}).catch(()=>{if(active)setDismissedState({userId:user.id,keys:[]});});return()=>{active=false;};},[user?.id]);
  const notificationKey=notification=>notification.key.startsWith('notification:')?notification.key:[user?.shopId,notification.key,notification.date?.toISOString(),notification.status].join(':');
  const dismissedKeys=dismissedState.userId===user?.id?dismissedState.keys:[];
  const notifications=allNotifications.filter(notification=>!dismissedKeys.includes(notificationKey(notification)));
  const notificationCount=notifications.length;
  const hasNotifications=notificationCount>0;
  const clearNotifications=async rows=>{
    if(notificationBusy || !rows.length)return false;
    setNotificationBusy(true);setNotificationError('');
    const keys=rows.map(notificationKey);const accountId=user?.id;
    try {for(let index=0;index<keys.length;index+=100)await api.post('/auth/notifications/dismissed',{keys:keys.slice(index,index+100)});if(currentUserId.current===accountId)setDismissedState(previous=>({userId:accountId,keys:[...(previous.userId===accountId?previous.keys:[]),...keys]}));return true;}
    catch(error){setNotificationError(error.response?.data?.message || 'Unable to clear notifications. Please retry.');return false;}
    finally{setNotificationBusy(false);}
  };
  const openNotification=async notification=>{if(await clearNotifications([notification])){setShowNotifications(false);navigate(notification.actionPath || '/dashboard/profile');}};

  const profilePercent = useMemo(() => {
    if (!user) return 0;
    if (typeof user.profileCompletionPercent === 'number') {
      return Math.min(Math.max(Math.round(user.profileCompletionPercent), 0), 100);
    }
    if (user.profileCompleted) {
      return 100;
    }
    return 0;
  }, [user]);

  const resolvedLandingPalette = { ...DEFAULT_LANDING_PALETTE, ...(landingPalette || {}) };
  const brandLabel = isShopUser ? (shopName || 'My Shop') : 'Link my Food';
  const hasSavedBrandVisibility = Object.prototype.hasOwnProperty.call(shopProfile?.publicPageDesign || {}, 'showBrandName');
  const showHeaderLogo = !isShopUser || shopHeaderDesign.showLogo !== false;
  const showHeaderBrandName = !isShopUser || (hasSavedBrandVisibility
    ? shopHeaderDesign.showBrandName !== false
    : shopProfile?.showShopNameOnPublicHeader !== false);
  const navbarClassName = 'shadow-sm';
  const headerBg = isShopUser && isShopThemeActive ? palette?.primary || resolvedLandingPalette.primary : resolvedLandingPalette.primary;
  const headerText = isShopUser && isShopThemeActive ? palette.onPrimary : resolvedLandingPalette.onPrimary || '#ffffff';
  const accentYellow = isShopUser && isShopThemeActive ? palette?.accent || palette?.font || resolvedLandingPalette.accent : resolvedLandingPalette.accent;
  const themeFontColor = isShopUser ? '#14283f' : headerText;
  const themePrimaryColor = palette?.primary || headerBg;
  const themeSecondaryColor = palette?.secondary || resolvedLandingPalette.accent || themePrimaryColor;
  const profileFontColor = palette.onBackground;
  const profileSecondaryFontColor = palette.onPrimary;
  const dropdownFontColor = isShopThemeActive ? profileFontColor : '#111827';
  const dropdownBackgroundColor = isShopUser ? '#ffffff' : isShopThemeActive ? palette.primary || '#ffffff' : '#f8f9fa';
  const dropdownHoverBackgroundColor = isShopThemeActive ? palette.background || '#ffffff' : '#eef2ff';
  const dropdownHeaderColor = isShopUser ? '#14283f' : isShopThemeActive ? profileSecondaryFontColor : '#111827';

  const NotificationToggle = useMemo(
    () =>
      forwardRef(({ onClick = () => {}, className = '', style: incomingStyle = {}, ...rest }, ref) => {
        const bellColor = isShopThemeActive
          ? themeSecondaryColor
          : hasNotifications
            ? '#800000'
            : '#6c757d';
        const mergedStyle = {
          ...incomingStyle,
          color: bellColor,
          '--notification-toggle-color': bellColor
        };
        return (
          <button
            type="button"
            className={`p-0 border-0 bg-transparent position-relative d-inline-flex align-items-center justify-content-center shop-notification-toggle ${className}`}
            {...rest}
            ref={ref}
            onClick={(event) => {
              event.preventDefault();
              onClick(event);
            }}
            aria-label={hasNotifications ? `${notificationCount} notifications` : 'Notifications'}
            style={mergedStyle}
          >
            <svg
              className="shop-notification-icon"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm6-6V11a6 6 0 0 0-5-5.91V4a1 1 0 1 0-2 0v1.09A6 6 0 0 0 6 11v5l-1.71 1.71A1 1 0 0 0 5 19h14a1 1 0 0 0 .71-1.71Z" />
            </svg>
            <span
              className="position-absolute shop-notification-count"
              style={{
                top: '100%',
                left: '50%',
                transform: 'translate(-50%, 1px)',
                fontSize: 10,
                fontWeight: 600,
                color: 'currentColor'
              }}
            >
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          </button>
        );
      }),
    [hasNotifications, notificationCount, isShopThemeActive, themeSecondaryColor]
  );

  const AvatarToggle = useMemo(
    () =>
      forwardRef(({ onClick, className = '', style: incomingStyle = {}, ...rest }, ref) => {
        const buttonColor = isShopThemeActive ? profileSecondaryFontColor : '#0d6efd';
        const mergedStyle = {
          ...incomingStyle,
          color: buttonColor,
          '--user-toggle-color': buttonColor
        };

        return (
          <button
            type="button"
            className={`btn p-0 border-0 bg-transparent position-relative shop-avatar-toggle dropdown-toggle ${className}`.trim()}
            {...rest}
            style={mergedStyle}
            ref={ref}
            onClick={(event) => {
              event.preventDefault();
              onClick(event);
            }}
            aria-label="User menu"
          >
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle fw-semibold overflow-hidden shop-avatar-placeholder"
              style={{
                width: 40,
                height: 40,
                backgroundColor: brandLogoTransparent ? 'transparent' : brandLogoBackground || (isShopThemeActive ? palette.background : '#ffffff'),
                color: isShopThemeActive ? palette.primary : '#0d6efd',
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              {accountPhoto ? (
                <img
                  src={accountPhoto}
                  alt="Your profile picture"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                initials
              )}
            </span>
          </button>
        );
      }),
    [initials, accountPhoto, shopLogoSrc, shopName, isShopThemeActive, profileSecondaryFontColor, palette.primary, palette.primaryBorder]
  );


  const shopHeaderLogoSize = isMobileView ? 50 : 80;
  const burgerLine = useMemo(
    () => ({
      base: {
        width: 26,
        height: 2,
        background: themeFontColor,
        borderRadius: 2,
        transition: 'transform 0.2s ease, opacity 0.2s ease'
      }
    }),
    [themeFontColor]
  );

  const renderBrandLogo = () => {
    if (isAdmin) {
      return <BrandLogo height={42} onDark showTagline />;
    }

    if (isShopUser && hasShopLogo) {
      return (
        <img
          src={shopLogoSrc}
          alt={`${shopName} logo`}
          style={{
            maxWidth: `${shopHeaderLogoSize}px`,
            maxHeight: `${shopHeaderLogoSize}px`,
            width: 'auto',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
      );
    }

    if (isShopUser) {
      return <BrandLogo height={42} showTagline />;
    }

    if (!brandLogoSrc) {
      return <i className="bi bi-egg-fried" />;
    }

    if (isShopThemeActive && themeFontColor) {
      return (
        <span
          aria-label="Brand logo"
          style={{
            display: 'block',
            width: brandLogoWidth ? `${brandLogoWidth}px` : '100%',
            height: brandLogoHeight ? `${brandLogoHeight}px` : '100%',
            backgroundColor: themeFontColor,
            WebkitMaskImage: `url(${brandLogoSrc})`,
            maskImage: `url(${brandLogoSrc})`,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskSize: 'contain'
          }}
        />
      );
    }

    return (
      <img
        src={brandLogoSrc}
        alt="Brand logo"
        style={{
          width: brandLogoWidth ? `${brandLogoWidth}px` : '100%',
          height: brandLogoHeight ? `${brandLogoHeight}px` : '100%',
          objectFit: 'contain',
          margin: 10
        }}
      />
    );
  };

  const NavigationPanel = isShopUser ? 'div' : Navbar.Collapse;
  return (
    <>
      <style>{`
        @media (max-width: 991px) {
          .admin-nav .nav-link-landing::after { width: 0 !important; opacity: 0 !important; }
        }
      `}</style>
      <Navbar
        expand="lg"
        expanded={navOpen}
        onToggle={setNavOpen}
        collapseOnSelect
        className={`${navbarClassName} admin-nav${isShopUser ? ' shop-workspace-header' : ''}`}
        data-bs-theme={isShopUser ? 'light' : 'dark'}
        style={{ position: 'relative', zIndex: 1060, backgroundColor: isShopUser ? '#ffffff' : headerBg, color: themeFontColor, '--header-brand': headerBg, '--header-accent': accentYellow }}
      >
      <Container fluid className="px-4">
        <Navbar.Brand
          as={NavLink}
          to={user?.role === 'FOOD_PLAZA' ? '/dashboard/food-plaza' : user?.role === 'SHOP_OPERATOR' ? '/dashboard/workspace' : isShopUser ? '/shope-dashboard' : '/'}
          className={`${isShopUser ? 'shop-header-brand' : 'd-flex align-items-center gap-2'} fw-bold`}
          style={{
            color: themeFontColor,
            fontFamily: 'Poppins, sans-serif',
            ...(isShopUser ? {
              '--shop-header-logo-width': `${shopHeaderDesign.logoWidth}px`,
              '--shop-header-logo-height': `${shopHeaderDesign.logoHeight}px`,
              '--shop-header-logo-position': shopHeaderDesign.logoPosition === 'right' ? 'end' : shopHeaderDesign.logoPosition === 'center' ? 'center' : 'start',
              '--shop-header-name-width': `${shopHeaderDesign.brandNameImageWidth}px`,
              '--shop-header-name-height': `${shopHeaderDesign.brandNameImageHeight}px`,
              '--shop-header-name-size': `${shopHeaderDesign.nameSize}px`,
              '--shop-header-name-position': shopHeaderDesign.namePosition === 'right' ? 'end' : shopHeaderDesign.namePosition === 'center' ? 'center' : 'start'
            } : {})
          }}
        >
          {showHeaderLogo && (
            <span
              className={isShopUser
                ? `shop-header-logo logo-shape-${shopHeaderDesign.logoShape}${shopHeaderDesign.logoBackground ? '' : ' no-logo-background'}${shopHeaderDesign.logoBorder ? '' : ' no-logo-border'}`
                : 'd-inline-flex align-items-center justify-content-center'}
              style={!isShopUser ? {
                width: isAdmin ? 190 : brandLogoWidth ? brandLogoWidth + 20 : 56,
                height: isAdmin ? 54 : brandLogoHeight ? brandLogoHeight + 20 : 56,
                backgroundColor: isAdmin ? 'transparent' : brandLogoTransparent ? 'transparent' : brandLogoBackground || themeFontColor,
                color: brandLogoTransparent ? themeFontColor : palette.primary,
                borderRadius: 10,
                padding: isAdmin ? 0 : 10,
                overflow: 'hidden'
              } : undefined}
            >
              {renderBrandLogo()}
            </span>
          )}
          {showHeaderBrandName && isShopUser && (
            <span className={`shop-header-brand-name${shopHeaderDesign.brandNameImageBackground ? ' has-brand-background' : ''}${shopHeaderDesign.brandNameImageBorder ? ' has-brand-border' : ''}`}>
              {shopBrandNameImageSrc
                ? <img src={shopBrandNameImageSrc} alt={brandLabel} />
                : <span style={{ color: themeFontColor }}>{brandLabel}</span>}
            </span>
          )}
          {!isShopUser && !isAdmin && showHeaderBrandName && <span style={{ color: themeFontColor }}>{brandLabel}</span>}
        </Navbar.Brand>
        <Navbar.Toggle
          ref={navToggleRef}
          aria-controls="main-nav"
          aria-expanded={navOpen}
          as="button"
          className="border-0 bg-transparent p-0 ms-auto d-lg-none"
        >
          <div style={{ position: 'relative', width: 26, height: 20 }}>
            <div
              style={{
                ...burgerLine.base,
                position: 'absolute',
                top: navOpen ? '50%' : 0,
                left: 0,
                transform: navOpen ? 'translateY(-50%) rotate(45deg)' : 'translateY(0) rotate(0)'
              }}
            />
            <div
              style={{
                ...burgerLine.base,
                position: 'absolute',
                top: '50%',
                left: 0,
                opacity: navOpen ? 0 : 1,
                transform: 'translateY(-50%)'
              }}
            />
            <div
              style={{
                ...burgerLine.base,
                position: 'absolute',
                bottom: navOpen ? '50%' : 0,
                left: 0,
                transform: navOpen ? 'translateY(50%) rotate(-45deg)' : 'translateY(0) rotate(0)'
              }}
            />
          </div>
        </Navbar.Toggle>
        {isShopUser && navOpen && (
          <button
            type="button"
            className="shop-mobile-nav-backdrop d-lg-none"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
          />
        )}
        <NavigationPanel id="main-nav" className={isShopUser ? `navbar-collapse collapse${navOpen ? ' show' : ''}` : undefined} inert={isShopUser && isMobileView && !navOpen ? '' : undefined}>
          {isShopUser && (
            <div className="shop-mobile-drawer-heading d-lg-none">
              <strong>Navigation</strong>
              <button type="button" aria-label="Close navigation" onClick={() => setNavOpen(false)}>
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          )}
          <Nav className="me-auto shop-navbar-links" style={{ color: themeFontColor }}>
            <DashboardNavLinks links={headerNavLinks} mobile={isMobileView} color={themeFontColor} onNavigate={() => setNavOpen(false)} />
          </Nav>
          {isShopUser && shopProfile?.preferredUrl && (
            <a
              className="shop-header-preview shop-mobile-public-menu d-lg-none"
              href={`/${encodeURIComponent(shopProfile.preferredUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Public menu
            </a>
          )}
        </NavigationPanel>
          <div className="d-flex align-items-center gap-3 shop-header-actions">
            {isShopUser && shopProfile?.preferredUrl && <a className="shop-header-preview" href={`/${encodeURIComponent(shopProfile.preferredUrl)}`} target="_blank" rel="noopener noreferrer"><i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Public menu</a>}
            {user ? (
              <>
                {isShopUser && <StatusSwitch className="shop-header-status align-items-center" showError />}
                <Dropdown
                  align="end"
                  show={showNotifications}
                  onToggle={(nextShow) => setShowNotifications(nextShow)}
                  className="notification-dropdown"
                >
                  <Dropdown.Toggle
                    as={NotificationToggle}
                    id="notification-toggle"
                    onClick={() => setShowNotifications((prev) => !prev)}
                    className="position-relative"
                    style={{ color: isShopThemeActive ? themeSecondaryColor : hasNotifications ? profileSecondaryFontColor : themeFontColor }}
                  />
                  <Dropdown.Menu
                    className="shadow-sm border-0 shop-dropdown-menu notification-menu"
                    style={{
                      minWidth: 280,
                      position: 'absolute',
                      top: 'calc(100% + 12px)',
                      right: isMobileView ? 'auto' : 0,
                      left: isMobileView ? 10 : 'auto',
                      backgroundColor: dropdownBackgroundColor,
                      color: dropdownHeaderColor,
                      '--notification-arrow-color': dropdownBackgroundColor,
                      '--notification-menu-font-color': dropdownHeaderColor,
                      '--notification-menu-hover-bg': dropdownHoverBackgroundColor,
                      '--notification-menu-hover-color': dropdownFontColor
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"><strong>Notifications</strong><Button size="sm" variant="link" disabled={notificationBusy||!notifications.length} onClick={()=>clearNotifications(notifications)}>Clear all</Button></div>
                    {notificationError&&<Alert variant="danger" className="m-2 small">{notificationError}</Alert>}
                    {notifications.length === 0 ? (
                      <div className="px-3 py-2 text-muted small">No notifications.</div>
                    ) : (
                      notifications.map((notification) => (
                        <div key={notification.key} className="small px-3 py-2 border-bottom"><button type="button" className="notification-open-row" disabled={notificationBusy} onClick={()=>openNotification(notification)}>
                          <div
                            className="fw-bold"
                            style={{
                              color: dropdownHeaderColor
                            }}
                          >
                            {notification.title}
                          </div>
                          <div
                            style={{
                              color: dropdownHeaderColor
                            }}
                          >
                            {notification.message}
                          </div>
                          <span className="d-block small mt-1">Open <i className="bi bi-arrow-right" aria-hidden="true"/></span></button><Button size="sm" variant="link" className="px-0" disabled={notificationBusy} onClick={()=>clearNotifications([notification])}>Clear</Button>
                        </div>
                      ))
                    )}
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown align="end" className="user-dropdown">
                  <Dropdown.Toggle as={AvatarToggle} id="user-menu-toggle" />
                <Dropdown.Menu
                  className="shadow-sm border-0 shop-dropdown-menu user-account-menu"
                  style={{
                    minWidth: 280,
                    width: 330,
                    position: 'absolute',
                    top: 'calc(100% + 12px)',
                    right: isMobileView ? 'auto' : 0,
                    left: isMobileView ? 10 : 'auto',
                    backgroundColor: dropdownBackgroundColor,
                    color: dropdownHeaderColor,
                    '--notification-arrow-color': dropdownBackgroundColor,
                    '--user-menu-font-color': dropdownFontColor,
                    '--user-menu-muted-color': dropdownHeaderColor,
                    '--user-menu-bg-color': dropdownBackgroundColor,
                    '--user-menu-header-color': dropdownHeaderColor,
                    '--user-menu-hover-bg': dropdownHoverBackgroundColor,
                    '--user-menu-progress-color': dropdownHeaderColor
                  }}
                >
                    <div className={`px-3 py-2${shopLogoSrc ? ' text-center' : ''}`}>
                      {accountPhoto && (
                        <div className="d-flex justify-content-center mb-2">
                          <div
                            className="rounded-circle overflow-hidden border"
                            style={{ width: 56, height: 56, backgroundColor: isShopThemeActive ? palette.background : '#ffffff' }}
                          >
                            <img src={accountPhoto} alt="Your profile picture" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        </div>
                      )}
                      <div className="fw-semibold" style={{ color: dropdownHeaderColor }}>{isStaffUser ? ownStaffAccount?.user?.name || displayName : shopName}</div>
                      <div
                        className="small"
                        style={{ color: dropdownHeaderColor }}
                      >
                        {displayName}
                      </div>
                      {!isAdmin && !isStaffUser && (
                        <div className="mt-2">
                          <div
                            className="d-flex justify-content-between align-items-center small mb-1"
                            style={{ color: dropdownHeaderColor }}
                          >
                            <span>Profile completion</span>
                            <span className="user-account-progress-percent">
                              {profilePercent}%
                            </span>
                          </div>
                          <ProgressBar
                            now={profilePercent}
                            style={{ height: 6 }}
                            className="user-account-progress"
                          />
                        </div>
                      )}
                    </div>
                    {!isAdmin && <Dropdown.Divider />}
                    {!isAdmin && (
                      <Dropdown.Item
                        className="user-account-menu-item"
                        onClick={() => {
                          setNavOpen(false);
                          if (isStaffUser) setStaffProfileOpen(true); else navigate(user?.role === 'FOOD_PLAZA' ? '/dashboard/food-plaza' : '/dashboard/profile');
                        }}
                        style={{ color: dropdownHeaderColor }}
                      >
                        View Profile
                      </Dropdown.Item>
                    )}
                    {canUseMpin && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Header>Privacy &amp; Security</Dropdown.Header>
                        <Dropdown.Item
                          className="user-account-menu-item"
                          onClick={() => openMpinModal('manual')}
                          style={{ color: dropdownHeaderColor }}
                        >
                          {user?.mpinSet ? 'Change MPIN' : 'Set MPIN'}
                        </Dropdown.Item>
                      </>
                    )}
                    <Dropdown.Item
                      className="user-account-menu-item"
                      onClick={() => {
                        setNavOpen(false);
                        navigate('/auth/reset');
                      }}
                      style={{ color: dropdownHeaderColor }}
                    >
                      Change Password
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      className="user-account-menu-item"
                      onClick={handleLogout}
                      style={{ color: dropdownHeaderColor }}
                    >
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </>
            ) : (
              <>
                <Button
                  as={NavLink}
                  to="/auth/login"
                  style={{ backgroundColor: headerBg, borderColor: '#fff', color: '#fff', fontWeight: 700 }}
                  variant="outline-light"
                >
                  Log In
                </Button>
                <Button
                  as={NavLink}
                  to="/shops/register"
                  style={{ backgroundColor: accentYellow, borderColor: accentYellow, color: headerBg, fontWeight: 800, borderRadius: 999 }}
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
      </Container>
    </Navbar>
    {isShopUser && user?.role !== 'SHOP_OPERATOR' && <section className="shop-status-hero" aria-label="Shop availability"><span>Shop availability</span><StatusSwitch showError /></section>}
    {isShopUser && shopStatus === 'failed' && <Alert variant="warning" className="mx-3 my-2 d-flex flex-wrap align-items-center justify-content-between gap-2" role="alert"><span>Unable to load your shop profile. Check your connection or sign in again if your session has ended.</span><Button size="sm" variant="outline-primary" onClick={()=>dispatch(clearShop())}>Retry</Button></Alert>}
      <Modal show={staffProfileOpen && isStaffUser} onHide={() => setStaffProfileOpen(false)} centered scrollable className="professional-modal"><Modal.Header closeButton><Modal.Title>My staff account</Modal.Title></Modal.Header><Modal.Body>{accountPhoto && <img src={accountPhoto} alt="Your profile picture" className="rounded-circle mb-3" style={{width:80,height:80,objectFit:'cover'}} />}<h5>{ownStaffAccount?.user?.name || displayName}</h5><p>{ownStaffAccount?.user?.email || user?.email}<br />{ownStaffAccount?.user?.phone || user?.phone}</p><h6>My assigned duties</h6><p>{(ownStaffAccount?.user?.staffModules || user?.staffModules || []).join(', ') || 'No duties assigned'}</p><h6>My recent activity</h6>{ownStaffAccount ? ownStaffAccount.activity?.length ? ownStaffAccount.activity.map(row => <div key={row._id} className="border-bottom py-2"><div>{row.description || row.action}</div><small className="text-muted">{new Date(row.createdAt).toLocaleString()}</small></div>) : <p className="text-muted">No recent activity.</p> : <p className="text-muted">Profile details are unavailable. Please try again.</p>}</Modal.Body></Modal>
      <Modal show={mpinModalOpen} onHide={closeMpinModal} centered backdrop={mpinPromptMode === 'first-login' ? 'static' : true}>
        <Form onSubmit={handleMpinSubmit}>
          <Modal.Header closeButton={mpinPromptMode !== 'first-login'}>
            <Modal.Title>{user?.mpinSet ? 'Change MPIN' : 'Set MPIN'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {mpinPromptMode === 'first-login' && !user?.mpinSet && (
              <Alert variant="info">
                Create a 6-digit MPIN for quicker secure login next time.
              </Alert>
            )}
            {mpinMessage && (
              <Alert variant={mpinStatus === 'failed' ? 'danger' : 'success'}>
                {mpinMessage}
              </Alert>
            )}
            {user?.mpinSet && (
              <Form.Group className="mb-3" controlId="accountCurrentMpin">
                <Form.Label>Current MPIN</Form.Label>
                <Form.Control
                  type="password"
                  name="currentMpin"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  title="Enter exactly 6 digits"
                  maxLength={6}
                  value={mpinForm.currentMpin}
                  onChange={handleMpinChange}
                  placeholder="Enter current MPIN"
                  autoComplete="current-password"
                  required
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3" controlId="accountNewMpin">
              <Form.Label>New MPIN</Form.Label>
              <Form.Control
                type="password"
                name="mpin"
                inputMode="numeric"
                pattern="[0-9]{6}"
                title="Enter exactly 6 digits"
                maxLength={6}
                value={mpinForm.mpin}
                onChange={handleMpinChange}
                placeholder="Enter 6-digit MPIN"
                autoComplete="new-password"
                required
              />
            </Form.Group>
            <Form.Group controlId="accountConfirmMpin">
              <Form.Label>Confirm MPIN</Form.Label>
              <Form.Control
                type="password"
                name="confirm"
                inputMode="numeric"
                pattern="[0-9]{6}"
                title="Enter exactly 6 digits"
                maxLength={6}
                value={mpinForm.confirm}
                onChange={handleMpinChange}
                placeholder="Re-enter MPIN"
                autoComplete="new-password"
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            {mpinPromptMode === 'first-login' && (
              <Button variant="outline-secondary" type="button" onClick={closeMpinModal} disabled={mpinStatus === 'loading'}>
                Later
              </Button>
            )}
            {mpinPromptMode !== 'first-login' && (
              <Button variant="outline-secondary" type="button" onClick={closeMpinModal} disabled={mpinStatus === 'loading'}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={mpinStatus === 'loading'}>
              {mpinStatus === 'loading' ? 'Saving...' : 'Save MPIN'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal show={statusModalOpen} onHide={() => !statusUpdating && setStatusModalOpen(false)} centered contentClassName="shop-status-modal-content border-0">
        <Modal.Header closeButton className="shop-status-modal-header">
          <Modal.Title>{shopIsOpen ? 'Close shop' : 'Open shop'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {shopProfile?.statusAutomation?.enabled && <Alert variant="info">Automatic working hours are enabled. This action creates an override for today.</Alert>}
          {statusError && <Alert variant="danger">{statusError}</Alert>}
          <p className="text-muted small">Customers will see this reason on the public menu when the shop is closed.</p>
          <Form.Group className="mb-3">
            <Form.Label>Reason</Form.Label>
            <Form.Select value={statusReason} onChange={(event) => { setStatusReason(event.target.value); setStatusOtherReason(''); }} autoFocus>
              {getStatusReasons(!shopIsOpen).map((reason) => <option key={reason} value={reason}>{reason}</option>)}
            </Form.Select>
          </Form.Group>
          {statusReason === OTHER_STATUS_REASON && <Form.Group><Form.Label>Other reason</Form.Label><Form.Control as="textarea" rows={3} minLength={3} maxLength={300} value={statusOtherReason} onChange={(event) => setStatusOtherReason(event.target.value)} placeholder="Enter a clear reason for customers and status history" autoFocus /><Form.Text>Enter between 3 and 300 characters.</Form.Text></Form.Group>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setStatusModalOpen(false)} disabled={statusUpdating}>Cancel</Button>
          <Button variant={shopIsOpen ? 'danger' : 'success'} onClick={handleToggleShopStatus} disabled={statusUpdating || resolveStatusReason(statusReason, statusOtherReason).length < 3}>{statusUpdating ? 'Updating...' : shopIsOpen ? 'Close shop' : 'Open shop'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TopNav;

